import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Download, MessageSquare, Share2, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useReviews } from '../hooks/useReviews';
import { useExtension } from '../hooks/useExtension';
import { doc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import toast from 'react-hot-toast';
import ContactForm from '../components/ContactForm';
import Navbar from '../components/Navbar';
import DownloadWarningModal from '../components/DownloadWarningModal';

declare global {
  interface Window {
    extensions: StoredExtension[];
  }
}

interface StoredExtension {
  name: string;
  code: string;
  enabled: boolean;
  version?: string;
  author?: string;
}

function ExtensionDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading, openAuthModal } = useAuth();
  const { extension, loading: extensionLoading, error: extensionError } = useExtension(id || '');
  const { reviews, loading: reviewsLoading, addReview } = useReviews(id || '');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  const [isDownloadWarningOpen, setIsDownloadWarningOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Load thumbnail URL
  useEffect(() => {
    if (extension?.thumbnailUrl) {
      const loadThumbnail = async () => {
        try {
          let thumbnailPath = extension.thumbnailUrl;
          if (thumbnailPath.startsWith('gs://extension-store-a9909.firebasestorage.app/')) {
            thumbnailPath = thumbnailPath.replace('gs://extension-store-a9909.firebasestorage.app/', '');
          } else if (!thumbnailPath.includes('/')) {
            thumbnailPath = `thumbnails/${thumbnailPath}`;
          }
          
          const thumbnailRef = ref(storage, thumbnailPath);
          const url = await getDownloadURL(thumbnailRef);
          
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = url;
          
          img.onload = () => {
            setThumbnailUrl(url);
          };
          
          img.onerror = () => {
            const altPath = thumbnailPath.replace('thumbnails/', '');
            const altRef = ref(storage, `thumbnails/${altPath}`);
            getDownloadURL(altRef)
              .then(altUrl => {
                const altImg = new Image();
                altImg.crossOrigin = 'anonymous';
                altImg.src = altUrl;
                altImg.onload = () => {
                  setThumbnailUrl(altUrl);
                };
                altImg.onerror = () => {
                  setThumbnailUrl('/placeholder-thumbnail.png');
                };
              })
              .catch(() => {
                setThumbnailUrl('/placeholder-thumbnail.png');
              });
          };
        } catch {
          setThumbnailUrl('/placeholder-thumbnail.png');
        }
      };
      loadThumbnail();
    }
  }, [extension?.thumbnailUrl]);

  if (authLoading) {
    return <div className="text-center py-12">Loading authentication...</div>;
  }

  if (!id) {
    return <div className="text-center py-12 text-red-600">Extension ID is required</div>;
  }

  if (extensionLoading) {
    return <div className="text-center py-12">Loading extension...</div>;
  }

  if (extensionError || !extension) {
    return <div className="text-center py-12 text-red-600">{extensionError || 'Extension not found'}</div>;
  }

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please log in to submit a review');
      return;
    }

    if (!user.uid) {
      toast.error('Unable to submit review. Please try logging in again.');
      return;
    }

    if (!rating) {
      toast.error('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please write a review');
      return;
    }

    const success = await addReview(
      user.uid,
      user.displayName || 'Anonymous',
      user.photoURL || '',
      rating,
      comment.trim(),
      user.icon || '👤'
    );

    if (success) {
      setRating(0);
      setComment('');
    }
  };

  const initiateDownload = async () => {
    if (!user) {
      toast.error('Please login to download extensions');
      openAuthModal();
      return;
    }
    setIsDownloadWarningOpen(true);
  };

  const handleDownload = async () => {
    let storagePath = '';
    try {
      setIsDownloading(true);
      storagePath = extension.downloadUrl;
      
      // Handle different URL formats
      if (storagePath.startsWith('gs://extension-store-a9909.firebasestorage.app/')) {
        storagePath = storagePath.replace('gs://extension-store-a9909.firebasestorage.app/', '');
      } else if (!storagePath.includes('/')) {
        storagePath = `extensions/${storagePath}`;
      }

      const fileRef = ref(storage, storagePath);
      const url = await getDownloadURL(fileRef);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = storagePath.split('/').pop() || 'extension.js';
      link.setAttribute('type', 'application/javascript');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(objectUrl);
      
      // Update download count
      const extensionRef = doc(db, 'extensions', extension.id);
      await updateDoc(extensionRef, {
        downloads: increment(1),
        updatedAt: serverTimestamp()
      });
      
      toast.success('Extension downloaded successfully. Use the Extension Manager in your whiteboard app to install it.');
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') {
        // Try direct filename
        try {
          const fileName = storagePath.split('/').pop() || '3d-viewer-extension.js';
          const altFileRef = ref(storage, `extensions/${fileName}`);
          const url = await getDownloadURL(altFileRef);
          
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const blob = await response.blob();
          
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = fileName;
          link.setAttribute('type', 'application/javascript');
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          URL.revokeObjectURL(objectUrl);
          
          toast.success('Extension downloaded successfully. Use the Extension Manager in your whiteboard app to install it.');
          return;
        } catch {
          toast.error('Extension file not found. Please contact support.');
        }
      } else {
        toast.error('Failed to download extension. Please try again later.');
      }
    } finally {
      setIsDownloading(false);
      setIsDownloadWarningOpen(false);
    }
  };

  const handleShare = async () => {
    try {
      const shareData = {
        title: extension.name,
        text: extension.description,
        url: window.location.href
      };

      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard');
      }
    } catch {
      toast.error('Failed to share');
    }
  };

  const handleContactClick = () => {
    toast.success(
      <div className="text-center">
        <p className="font-medium mb-1">Coming Soon! 🎉</p>
        <p className="text-sm">We're excited to hear from you! Our contact system is being enhanced to serve you better.</p>
        <p className="text-xs mt-1 text-gray-500">Stay tuned for updates.</p>
      </div>,
      {
        duration: 5000,
        icon: '✉️'
      }
    );
  };

  const handleSearch = (query: string) => {
    // Navigate to home page with search query
    navigate(`/?search=${encodeURIComponent(query)}`);
  };

  return (
    <>
      <Navbar onSearch={handleSearch} />
      <DownloadWarningModal
        isOpen={isDownloadWarningOpen}
        onClose={() => setIsDownloadWarningOpen(false)}
        onConfirm={handleDownload}
        extensionName={extension.name}
        isDownloading={isDownloading}
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={extension.name}
              className="w-full h-64 object-cover"
              crossOrigin="anonymous"
            />
          ) : (
            <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400">Loading thumbnail...</span>
            </div>
          )}
          
          <div className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{extension.name}</h1>
                <p className="text-gray-600 mt-2">by {extension.author}</p>
              </div>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">
                {extension.category}
              </span>
            </div>

            <div className="flex items-center space-x-6 mt-4">
              <div className="flex items-center space-x-1">
                <Star className="w-5 h-5 text-yellow-400 fill-current" />
                <span className="text-gray-600">
                  {(extension.averageRating || 0).toFixed(1)}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Download className="w-5 h-5 text-gray-600" />
                <span className="text-gray-600">{extension.downloads || 0}</span>
              </div>
              <div className="flex items-center space-x-1">
                <MessageSquare className="w-5 h-5 text-gray-600" />
                <span className="text-gray-600">{extension.totalRatings || 0} Reviews</span>
              </div>
            </div>

            <div className="mt-6">
              <h2 className="text-xl font-semibold text-gray-900">Description</h2>
              <p className="mt-2 text-gray-600">{extension.description}</p>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={initiateDownload}
                disabled={isDownloading}
                className={`flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg flex items-center justify-center space-x-2 ${
                  isDownloading ? 'opacity-90 cursor-not-allowed' : 'hover:bg-indigo-700'
                }`}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    <span>Download v{extension.version}</span>
                  </>
                )}
              </button>
              <button 
                onClick={handleShare}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
              <button 
                onClick={handleContactClick}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 group relative"
              >
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="text-gray-600">Contact Us</span>
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full group-hover:animate-ping"></span>
              </button>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Reviews</h2>
              
              {user ? (
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rating</label>
                    <div className="flex space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className={`p-1 ${
                            rating >= star ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                        >
                          <Star className="w-6 h-6 fill-current" />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Your Review</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="Write your review..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Submit Review
                  </button>
                </form>
              ) : (
                <button
                  onClick={openAuthModal}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  Sign in to leave a review
                </button>
              )}

              {reviewsLoading ? (
                <div className="mt-6 text-center">Loading reviews...</div>
              ) : reviews.length > 0 ? (
                <div className="mt-6 space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="flex space-x-4">
                      {review.userPhotoURL ? (
                        <img
                          src={review.userPhotoURL}
                          alt={review.userDisplayName}
                          className="w-12 h-12 rounded-full"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
                          <span className="text-2xl select-none">
                            {review.userIcon || '👤'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-gray-900">{review.userDisplayName}</div>
                            <div className="flex text-yellow-400">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-600 mt-1">{review.comment}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {review.createdAt?.toDate?.() 
                            ? review.createdAt.toDate().toLocaleDateString() 
                            : 'Date not available'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-6 text-center text-gray-600">
                  No reviews yet. Be the first to review!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isContactFormOpen && (
        <ContactForm
          isOpen={isContactFormOpen}
          onClose={() => setIsContactFormOpen(false)}
          initialType="general"
          relatedItem={{
            id: extension.id,
            type: 'extension',
            name: extension.name
          }}
        />
      )}
    </>
  );
}

export default ExtensionDetails;